/**
 * Column adapter for translating custom Column<T> to TanStack Table ColumnDef<T>
 *
 * This adapter allows gradual migration to TanStack Table while maintaining
 * compatibility with existing column definitions.
 */
import {
  AccessorKeyColumnDef,
  CellContext,
  ColumnDef,
} from '@tanstack/react-table';

import { Column } from '@/components/table/types';
import { formatDate } from '@/utils/date.util';

/**
 * Translates a custom Column<TData> to TanStack Table ColumnDef<TData>
 *
 * @param column - Custom column definition
 * @param columnIndex - Optional column index for stable fallback IDs
 * @returns TanStack Table column definition
 */
export function adaptColumnToTanStack<TData>(
  column: Column<TData>,
  columnIndex: number = 0
): ColumnDef<TData, unknown> {
  const columnId = column.id !== '' ? String(column.id) : `col-${columnIndex}`;

  // Build column definition object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columnDef: AccessorKeyColumnDef<TData, any> = {
    id: columnId,
    header: () => column.label,
    accessorKey: column.id !== '' ? (column.id as string) : columnId,
  };

  // Handle width configurations
  if (column.fixedWidth) {
    columnDef.size = column.fixedWidth;
    columnDef.minSize = column.fixedWidth;
    columnDef.maxSize = column.fixedWidth;
  } else {
    if (column.isExpandable?.minWidth) {
      columnDef.minSize = column.isExpandable.minWidth;
    } else if (column.minWidth) {
      columnDef.minSize = column.minWidth;
    }

    if (column.maxWidth) {
      columnDef.maxSize = column.maxWidth;
    }
  }

  // Determine cell type
  const cellType = column.cell;

  // Handle cell rendering based on type
  if (cellType === 'date') {
    columnDef.cell = (info: CellContext<TData, unknown>) => {
      const value = info.getValue();
      if (!value) return '-';
      return formatDate(String(value));
    };
  } else if (cellType === 'highlight') {
    // Highlight cell type - render as is for now
    columnDef.cell = (info: CellContext<TData, unknown>) => {
      const value = info.getValue();
      return value != null ? String(value) : '-';
    };
  } else if (typeof cellType === 'function') {
    columnDef.cell = (info: CellContext<TData, unknown>) => {
      return cellType(info.row.original);
    };
  } else {
    // Default: render accessor value
    columnDef.cell = (info: CellContext<TData, unknown>) => {
      const value = info.getValue();
      return value != null ? String(value) : '-';
    };
  }

  // Enable sorting if configured
  if (column.sorting !== undefined) {
    columnDef.enableSorting = column.sorting;
  }

  return columnDef;
}

/**
 * Batch convert multiple columns
 *
 * @param columns - Array of custom column definitions
 * @returns Array of TanStack Table column definitions
 */
export function adaptColumnsToTanStack<TData>(
  columns: Column<TData>[]
): ColumnDef<TData>[] {
  return columns.map((column, index) => adaptColumnToTanStack(column, index));
}
